import { apiFetch } from "../../utils/apiFetch";
import { buildAssetUrl } from "../../utils/api";

const SUBSCRIPTIONS_BASE = "api/v1/customer/subscriptions";

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeProduct = (product = {}) => {
  const imageUrls = [
    product.image1,
    product.image2,
    product.image3,
    product.image4,
    product.image5,
    product.image6,
  ]
    .filter(Boolean)
    .map(buildAssetUrl);

  return {
    ...product,
    product_id: product.product_id ?? product.id,
    name: product.name ?? product.product_name ?? "Product",
    image: imageUrls[0] || null,
    images: imageUrls,
    variants: asArray(product.variants).map((variant) => ({
      ...variant,
      product_variant_id:
        variant.product_variant_id ?? variant.id ?? variant.variant_id,
      variant_id: variant.variant_id ?? variant.product_variant_id ?? variant.id,
      variant_name: variant.variant_name ?? variant.name ?? "Default",
      unit_name: variant.unit_name ?? variant.unit ?? "",
      original_price: variant.original_price ?? variant.price ?? "0",
      discount_price:
        variant.discount_price ?? variant.sale_price ?? variant.price ?? "0",
    })),
  };
};

const normalizeSubscription = (subscription = {}) => ({
  ...subscription,
  subscription_id: subscription.subscription_id ?? subscription.id,
  product_name:
    subscription.product_name ?? subscription.name ?? subscription.product?.name,
  image1: buildAssetUrl(subscription.image1 || subscription.product?.image1),
  quantity: subscription.quantity ?? "1.00",
  quantity_unit: subscription.quantity_unit ?? subscription.unit_name ?? "litre",
  frequency: subscription.frequency ?? "daily",
  plan_type: subscription.plan_type ?? "monthly",
  delivery_slot: subscription.delivery_slot ?? "06:00-08:00",
  payment_method: subscription.payment_method ?? "monthly_billing",
  status: subscription.status ?? "active",
});

const normalizeDeliveryPage = (payload = {}) => {
  const data = payload.data ?? payload;
  return {
    total: Number(data.total ?? 0),
    page: Number(data.page ?? 1),
    limit: Number(data.limit ?? 20),
    deliveries: asArray(data.deliveries),
  };
};

// Default fallback values used if /config fails or hasn't loaded yet
const DEFAULT_CONFIG = {
  products: [],
  plans: [
    { plan_id: 1, plan_name: "Weekly Subscription", plan_key: "weekly", duration_days: 7 },
    { plan_id: 2, plan_name: "Monthly Subscription", plan_key: "monthly", duration_days: 30 },
  ],
  quantities: [
    { qty_id: 1, label: "500 ml", value: "0.50", unit: "litre", is_custom: false },
    { qty_id: 2, label: "1 Litre", value: "1.00", unit: "litre", is_custom: false },
    { qty_id: 3, label: "2 Litres", value: "2.00", unit: "litre", is_custom: false },
    { qty_id: 4, label: "Custom", value: "0.00", unit: "litre", is_custom: true },
  ],
  frequencies: [
    { freq_id: 1, label: "Daily", freq_key: "daily" },
    { freq_id: 2, label: "Alternate Day", freq_key: "alternate_day" },
    { freq_id: 3, label: "Weekly", freq_key: "weekly" },
  ],
  delivery_slots: [
    { slot_id: 1, label: "6 AM - 8 AM", slot_key: "06:00-08:00" },
    { slot_id: 2, label: "8 AM - 10 AM", slot_key: "08:00-10:00" },
  ],
  payment_methods: [
    { pm_id: 1, label: "UPI AutoPay", pm_key: "upi_autopay", is_recommended: true },
    { pm_id: 2, label: "Wallet", pm_key: "wallet", is_recommended: false },
    { pm_id: 3, label: "Monthly Billing", pm_key: "monthly_billing", is_recommended: false },
  ],
};

const updateSubscriptionInList = (subscriptions, updated) => {
  const normalized = normalizeSubscription(updated);
  return subscriptions.map((subscription) =>
    String(subscription.subscription_id) === String(normalized.subscription_id)
      ? { ...subscription, ...normalized }
      : subscription,
  );
};

export const createSubscriptionsSlice = (set, get) => ({
  // ── Config (STEP 0) ──────────────────────────────────────────────────────────
  subscription_config: DEFAULT_CONFIG,
  subscription_config_loading: false,
  subscription_config_error: null,

  // ── Products (derived from config, kept for compat) ──────────────────────────
  subscription_products: [],
  subscription_products_loading: false,
  subscription_products_error: null,

  // ── List ─────────────────────────────────────────────────────────────────────
  subscriptions: [],
  subscriptions_loading: false,
  subscriptions_error: null,

  // ── Detail ───────────────────────────────────────────────────────────────────
  subscription_detail: null,
  subscription_detail_loading: false,

  // ── Deliveries ───────────────────────────────────────────────────────────────
  subscription_deliveries: {
    total: 0,
    page: 1,
    limit: 20,
    deliveries: [],
  },
  subscription_deliveries_loading: false,

  // ── Mutation loading ─────────────────────────────────────────────────────────
  subscription_action_loading: false,

  // ── STEP 0: Fetch config + full product list ─────────────────────────────────
  fetchSubscriptionConfig: async () => {
    set({
      subscription_config_loading: true,
      subscription_products_loading: true,
      subscription_config_error: null,
      subscription_products_error: null,
    });

    try {
      // Fire both requests in parallel — config for plans/slots/etc., products for full list
      const [configJson, productsJson] = await Promise.allSettled([
        apiFetch(`${SUBSCRIPTIONS_BASE}/config`, { auth: false }),
        apiFetch(`${SUBSCRIPTIONS_BASE}/products`, { auth: false }),
      ]);

      // ── Config data ──────────────────────────────────────────────────────────
      const raw = configJson.status === "fulfilled" ? (configJson.value.data ?? {}) : {};
      const configProducts = asArray(raw.products).map(normalizeProduct);

      // ── Products data (may return more items than config) ────────────────────
      const extraProducts =
        productsJson.status === "fulfilled"
          ? asArray(productsJson.value.data).map(normalizeProduct)
          : [];

      // Merge: config products first (they have the most complete data),
      // then append any extra products not already present in the config list
      const configIds = new Set(configProducts.map((p) => String(p.product_id)));
      const mergedProducts = [
        ...configProducts,
        ...extraProducts.filter((p) => !configIds.has(String(p.product_id))),
      ];

      const config = {
        products: mergedProducts,
        plans: asArray(raw.plans).length ? raw.plans : DEFAULT_CONFIG.plans,
        quantities: asArray(raw.quantities).length ? raw.quantities : DEFAULT_CONFIG.quantities,
        frequencies: asArray(raw.frequencies).length ? raw.frequencies : DEFAULT_CONFIG.frequencies,
        delivery_slots: asArray(raw.delivery_slots).length ? raw.delivery_slots : DEFAULT_CONFIG.delivery_slots,
        payment_methods: asArray(raw.payment_methods).length ? raw.payment_methods : DEFAULT_CONFIG.payment_methods,
      };

      set({
        subscription_config: config,
        subscription_products: mergedProducts,
        subscription_config_loading: false,
        subscription_products_loading: false,
      });

      return config;
    } catch (error) {
      set({
        subscription_config_error: error.message,
        subscription_products_error: error.message,
        subscription_config_loading: false,
        subscription_products_loading: false,
      });
      return DEFAULT_CONFIG;
    }
  },

  // ── API 1: Create / Activate ─────────────────────────────────────────────────
  createSubscription: async (payload) => {
    set({ subscription_action_loading: true, subscriptions_error: null });

    try {
      const json = await apiFetch(SUBSCRIPTIONS_BASE, {
        method: "POST",
        body: {
          product_id: Number(payload.product_id),
          product_variant_id: Number(payload.product_variant_id),
          address_id: Number(payload.address_id),
          quantity: Number(payload.quantity),
          quantity_unit: payload.quantity_unit || "litre",
          frequency: payload.frequency,
          plan_type: payload.plan_type,
          delivery_slot: payload.delivery_slot,
          payment_method: payload.payment_method,
          ...(payload.start_date ? { start_date: payload.start_date } : {}),
        },
      });

      const subscription = normalizeSubscription(json.data);
      set((state) => ({
        subscriptions: [subscription, ...state.subscriptions],
        subscription_detail: subscription,
        subscription_action_loading: false,
      }));
      // Return full response so the caller can access cashfree.subscription_session_id / payment_link
      return { subscription, raw: json.data };
    } catch (error) {
      set({
        subscriptions_error: error.message,
        subscription_action_loading: false,
      });
      throw error;
    }
  },

  // ── UPI AutoPay: Poll until mandate resolves ─────────────────────────────────
  // Polls GET /subscriptions/:id every 2.5 s for up to 15 s.
  // Returns the final status string: 'active' | 'pending_mandate' | 'cancelled' | 'failed' | ...
  verifySubscriptionStatus: async (subscriptionId) => {
    const MAX_ATTEMPTS = 6;
    const INTERVAL_MS = 2500;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      try {
        const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}`);
        const status = (json.data?.status ?? "pending_mandate").toLowerCase();
        const subscription = normalizeSubscription(json.data);

        // Keep store in sync on every poll
        set((state) => ({
          subscription_detail: subscription,
          subscriptions: updateSubscriptionInList(state.subscriptions, subscription),
        }));

        // Stop polling once mandate is no longer pending
        if (status !== "pending_mandate") return status;
      } catch {
        // Network blip — keep trying
      }
    }

    // Timed out — mandate still pending (Cashfree processes async)
    return "pending_mandate";
  },

  // ── API 2: List ──────────────────────────────────────────────────────────────
  fetchSubscriptions: async (status = "") => {
    set({ subscriptions_loading: true, subscriptions_error: null });

    try {
      const query = status ? `?status=${encodeURIComponent(status)}` : "";
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}${query}`);
      const subscriptions = asArray(json.data).map(normalizeSubscription);
      set({ subscriptions, subscriptions_loading: false });
      return subscriptions;
    } catch (error) {
      set({ subscriptions_error: error.message, subscriptions_loading: false });
      return [];
    }
  },

  // ── API 3: Detail ────────────────────────────────────────────────────────────
  fetchSubscriptionDetail: async (subscriptionId) => {
    set({ subscription_detail_loading: true, subscriptions_error: null });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}`);
      const subscription = normalizeSubscription(json.data);
      set({
        subscription_detail: subscription,
        subscription_detail_loading: false,
      });
      return subscription;
    } catch (error) {
      set({
        subscriptions_error: error.message,
        subscription_detail_loading: false,
      });
      throw error;
    }
  },

  // ── API 4: Pause ─────────────────────────────────────────────────────────────
  pauseSubscription: async (subscriptionId, payload) => {
    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/pause`, {
        method: "POST",
        body: {
          pause_from: payload.pause_from,
          pause_to: payload.pause_to,
          reason: payload.reason || "temporary_pause",
          ...(payload.comments?.trim()
            ? { comments: payload.comments.trim() }
            : {}),
        },
      });

      await get().fetchSubscriptionDetail(subscriptionId);
      await get().fetchSubscriptions();
      set({ subscription_action_loading: false });
      return json.data;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 5: Resume ────────────────────────────────────────────────────────────
  resumeSubscription: async (subscriptionId, resumeDate = "") => {
    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/resume`, {
        method: "POST",
        body: resumeDate ? { resume_date: resumeDate } : {},
      });

      await get().fetchSubscriptionDetail(subscriptionId);
      await get().fetchSubscriptions();
      set({ subscription_action_loading: false });
      return json.data;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 6: Change Quantity ───────────────────────────────────────────────────
  changeSubscriptionQuantity: async (subscriptionId, payload) => {
    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/quantity`, {
        method: "PATCH",
        body: {
          quantity: Number(payload.quantity),
          quantity_unit: payload.quantity_unit || "litre",
        },
      });

      const updated = normalizeSubscription(json.data);
      set((state) => ({
        subscriptions: updateSubscriptionInList(state.subscriptions, updated),
        subscription_detail:
          String(state.subscription_detail?.subscription_id) ===
          String(subscriptionId)
            ? { ...state.subscription_detail, ...updated }
            : state.subscription_detail,
        subscription_action_loading: false,
      }));
      return updated;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 7: Change Plan / Frequency / Slot ───────────────────────────────────
  changeSubscriptionPlan: async (subscriptionId, payload) => {
    const body = {};
    if (payload.plan_type) body.plan_type = payload.plan_type;
    if (payload.frequency) body.frequency = payload.frequency;
    if (payload.delivery_slot) body.delivery_slot = payload.delivery_slot;

    set({ subscription_action_loading: true });

    try {
      const json = await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}/plan`, {
        method: "PATCH",
        body,
      });

      const updated = normalizeSubscription(json.data);
      set((state) => ({
        subscriptions: updateSubscriptionInList(state.subscriptions, updated),
        subscription_detail:
          String(state.subscription_detail?.subscription_id) ===
          String(subscriptionId)
            ? { ...state.subscription_detail, ...updated }
            : state.subscription_detail,
        subscription_action_loading: false,
      }));
      return updated;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },

  // ── API 8: Delivery History ──────────────────────────────────────────────────
  fetchSubscriptionDeliveries: async (
    subscriptionId,
    { page = 1, limit = 20 } = {},
  ) => {
    set({ subscription_deliveries_loading: true });

    try {
      const json = await apiFetch(
        `${SUBSCRIPTIONS_BASE}/${subscriptionId}/deliveries?page=${page}&limit=${limit}`,
      );
      const deliveryPage = normalizeDeliveryPage(json);
      set({
        subscription_deliveries: deliveryPage,
        subscription_deliveries_loading: false,
      });
      return deliveryPage;
    } catch (error) {
      set({
        subscriptions_error: error.message,
        subscription_deliveries_loading: false,
      });
      throw error;
    }
  },

  // ── API 9: Cancel ────────────────────────────────────────────────────────────
  cancelSubscription: async (subscriptionId) => {
    set({ subscription_action_loading: true });

    try {
      await apiFetch(`${SUBSCRIPTIONS_BASE}/${subscriptionId}`, {
        method: "DELETE",
      });

      set((state) => ({
        subscriptions: state.subscriptions.map((subscription) =>
          String(subscription.subscription_id) === String(subscriptionId)
            ? { ...subscription, status: "cancelled" }
            : subscription,
        ),
        subscription_detail:
          String(state.subscription_detail?.subscription_id) ===
          String(subscriptionId)
            ? { ...state.subscription_detail, status: "cancelled" }
            : state.subscription_detail,
        subscription_action_loading: false,
      }));
      return true;
    } catch (error) {
      set({ subscriptions_error: error.message, subscription_action_loading: false });
      throw error;
    }
  },
});
